import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { comparisonFeatures, faqs } from '@/mocks/publicPricing';
import { SwalAlert } from '@/hooks/useSweetAlert';
import StreamingHostingSection from './components/StreamingHostingSection';
import { getPublicBillingPlans } from '@/services/billing';
import type { BillingPlan } from '@/types/billing';

function getPlanBadgeStyle(plan: BillingPlan) {
  if (plan.slug === 'pro') return 'bg-primary-500 text-background-50';
  if (plan.slug === 'business' || plan.slug === 'premium') return 'bg-accent-500 text-background-50';
  return 'bg-primary-500 text-background-50';
}

function getCheckStyle(plan: BillingPlan, isNegative: boolean) {
  if (plan.slug === 'pro') {
    if (isNegative) return 'bg-background-50/10 text-background-50/40';
    return 'bg-background-50/20 text-background-50';
  }
  if (isNegative) return 'bg-foreground-100 text-foreground-300';
  if (plan.slug === 'starter') return 'bg-primary-100 text-primary-600';
  if (plan.slug === 'business' || plan.slug === 'premium') return 'bg-accent-100 text-accent-600';
  return 'bg-secondary-100 text-secondary-600';
}

function getCtaStyle(plan: BillingPlan) {
  if (plan.slug === 'pro') return 'bg-background-50 text-primary-600 hover:bg-background-100 shadow-lg';
  if (plan.slug === 'starter') return 'bg-primary-500 text-background-50 hover:bg-primary-600 shadow-lg shadow-primary-500/20';
  if (plan.slug === 'business' || plan.slug === 'premium') return 'bg-accent-500 text-background-50 hover:bg-accent-600 shadow-lg shadow-accent-500/20';
  return 'bg-secondary-500 text-background-50 hover:bg-secondary-600 shadow-lg shadow-secondary-500/20';
}

function getPlanBadge(plan: BillingPlan): string {
  const badges: Record<string, string> = {
    free: 'Para empezar',
    starter: 'Crecimiento',
    pro: 'Más popular',
    business: 'Equipos',
    premium: 'Profesional',
  };
  return badges[plan.slug] || '';
}

function getPlanTagline(plan: BillingPlan): string {
  const taglines: Record<string, string> = {
    free: 'Tu primera frecuencia en la web',
    starter: 'Un paso más allá del free',
    pro: 'El plan más elegido por las radios',
    business: 'Para radios con equipo grande',
    premium: 'Máxima potencia, zero límites',
  };
  return taglines[plan.slug] || plan.description;
}

function getPlanFeatureLabels(plan: BillingPlan): { label: string; negative: boolean }[] {
  const labels: { label: string; negative: boolean }[] = [];

  if (plan.slug === 'free') {
    labels.push({ label: 'Perfil básico profesional', negative: false });
    labels.push({ label: 'Stream URL para transmitir', negative: false });
    labels.push({ label: 'Cabina básica con chat', negative: false });
    labels.push({ label: 'Sección de contacto', negative: false });
    labels.push({ label: 'Redes sociales integradas', negative: false });
    labels.push({ label: `${plan.storage_gb} GB almacenamiento`, negative: false });
    labels.push({ label: 'Fotos', negative: true });
    labels.push({ label: 'Videos', negative: true });
    labels.push({ label: 'Lives', negative: true });
  } else {
    labels.push({ label: plan.slug === 'starter' ? 'Todo lo de Free' : 'Todo lo del plan anterior', negative: false });
    if (plan.has_domain) labels.push({ label: 'Dominio personalizado', negative: false });
    else labels.push({ label: 'Dominio personalizado', negative: true });
    labels.push({ label: `${plan.storage_gb} GB almacenamiento`, negative: false });
    if (plan.has_lives) labels.push({ label: 'Lives y transmisiones', negative: false });
    else labels.push({ label: 'Lives', negative: true });
    if (plan.has_app) labels.push({ label: 'App individual', negative: false });
    else labels.push({ label: 'App individual', negative: true });
    if (plan.has_managed_streaming) labels.push({ label: 'Streaming administrado RFW', negative: false });
    if (plan.has_autodj) labels.push({ label: 'AutoDJ incluido', negative: false });
  }

  return labels;
}

function getPlanActivationInfo(plan: BillingPlan): { label: string; icon: string; approval: boolean } {
  if (plan.price_monthly === 0) {
    return { label: 'Sujeto a aprobación', icon: 'ri-time-line', approval: true };
  }
  return { label: 'Activación inmediata', icon: 'ri-flashlight-line', approval: false };
}

function getPlanCtaText(plan: BillingPlan): string {
  if (plan.price_monthly === 0) return 'Comenzar Gratis';
  return `Elegir ${plan.name}`;
}

function getPlanStorageLabel(plan: BillingPlan): string {
  if (plan.storage_gb >= 1024) return '1 TB';
  return `${plan.storage_gb} GB`;
}

export default function PricingPage() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState<string>('todas');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [billingMode, setBillingMode] = useState<'monthly' | 'yearly'>('monthly');
  const [billingPlans, setBillingPlans] = useState<BillingPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState<string | null>(null);
  const comparisonRef = useRef<HTMLElement>(null);

  useEffect(() => {
    let cancelled = false;
    setPlansLoading(true);
    setPlansError(null);
    getPublicBillingPlans()
      .then((plans) => {
        if (!cancelled) setBillingPlans(plans);
      })
      .catch((err) => {
        if (!cancelled) setPlansError(err instanceof Error ? err.message : 'Error al cargar planes');
      })
      .finally(() => {
        if (!cancelled) setPlansLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const isYearly = billingMode === 'yearly';
  const categories = ['todas', ...Array.from(new Set(comparisonFeatures.map((f) => f.category)))];

  const filteredComparison =
    activeCategory === 'todas'
      ? comparisonFeatures
      : comparisonFeatures.filter((f) => f.category === activeCategory);

  const renderComparisonValue = (value: string | boolean) => {
    if (value === true) {
      return (
        <div className="w-5 h-5 mx-auto rounded-full bg-green-100 flex items-center justify-center">
          <i className="ri-check-line text-green-600 text-xs font-bold"></i>
        </div>
      );
    }
    if (value === false) {
      return (
        <div className="w-5 h-5 mx-auto rounded-full bg-foreground-100 flex items-center justify-center">
          <i className="ri-close-line text-foreground-400 text-xs"></i>
        </div>
      );
    }
    return <span className="text-xs font-medium text-foreground-700">{value}</span>;
  };

  const handleSelectPlan = (plan: BillingPlan) => {
    const isFree = plan.price_monthly === 0;

    if (isFree) {
      SwalAlert.confirm(
        `¿Crear radio con plan ${plan.name}?`,
        `El plan ${plan.name} está sujeto a aprobación. Tu radio será revisada en 24-48 horas. ¿Querés continuar con el registro?`,
      ).then((result) => {
        if (result.isConfirmed) {
          navigate('/register-radio');
        }
      });
    } else if (isYearly && plan.price_yearly) {
      SwalAlert.confirm(
        `¿Crear radio con plan ${plan.name} Anual?`,
        `Elegiste la licencia anual de ${plan.formatted_price_yearly}. Ahorrás 2 meses respecto al pago mensual. ¿Confirmás y continuás al registro?`,
      ).then((result) => {
        if (result.isConfirmed) {
          navigate('/register-radio');
        }
      });
    } else {
      SwalAlert.confirm(
        `¿Crear radio con plan ${plan.name}?`,
        `El plan ${plan.name} tiene activación inmediata. Una vez completado el registro, tu radio estará lista al instante. ¿Continuamos?`,
      ).then((result) => {
        if (result.isConfirmed) {
          navigate('/register-radio');
        }
      });
    }
  };

  const scrollToComparison = () => {
    if (comparisonRef.current) {
      comparisonRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      <main className="flex-1">
        {/* ── HERO ── */}
        <section className="relative w-full pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden bg-background-100/70">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-primary-100/40 blur-3xl"></div>
            <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-accent-100/30 blur-3xl"></div>
          </div>

          <div className="relative w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-5">
              <i className="ri-price-tag-3-line text-xs"></i>
              Planes y precios
            </div>
            <h1 className="text-3xl md:text-5xl font-extrabold text-foreground-950 tracking-tight leading-tight">
            Hacé crecer tu{' '}
            <span className="text-primary-600">comunidad</span>
          </h1>
          <p className="mt-5 text-foreground-500 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            RFW es la red social de la radio. Elegí el plan según el tamaño de tu comunidad y las herramientas que necesitás para hacerla crecer. Sin costos ocultos, sin contratos forzosos.
          </p>
          </div>
        </section>

        {/* ── BILLING TOGGLE + PLAN CARDS ── */}
        <section className="relative -mt-10 pb-16 md:pb-24">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            {/* Billing toggle */}
            <div className="flex items-center justify-center mb-8">
              <div className="inline-flex items-center bg-background-100 rounded-full p-1 border border-background-200">
                <button
                  onClick={() => setBillingMode('monthly')}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    !isYearly ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-400 hover:text-foreground-600'
                  }`}
                >
                  Mensual
                </button>
                <button
                  onClick={() => setBillingMode('yearly')}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                    isYearly ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-400 hover:text-foreground-600'
                  }`}
                >
                  Anual
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-bold">-17%</span>
                </button>
              </div>
            </div>

            {/* Plan cards — dynamic grid */}
            {plansLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-sm text-foreground-400">Cargando planes...</p>
                </div>
              </div>
            ) : plansError ? (
              <div className="flex items-center justify-center py-16">
                <div className="flex flex-col items-center gap-3 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center">
                    <i className="ri-error-warning-line text-red-400 text-xl"></i>
                  </div>
                  <p className="text-sm text-foreground-600 font-medium">No se pudieron cargar los planes</p>
                  <p className="text-xs text-foreground-400">{plansError}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="mt-2 px-4 py-2 rounded-xl bg-primary-500 text-background-50 text-xs font-semibold cursor-pointer whitespace-nowrap"
                  >
                    Reintentar
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-5 items-start">
              {billingPlans.map((plan) => {
                const isHl = plan.slug === 'pro';
                const isFree = plan.price_monthly === 0;
                const activation = getPlanActivationInfo(plan);
                const featureLabels = getPlanFeatureLabels(plan);
                const badge = getPlanBadge(plan);
                const displayPrice = isYearly && plan.price_yearly
                  ? plan.formatted_price_yearly
                  : plan.formatted_price_monthly;
                const displayPeriod = isYearly
                  ? 'Pago anual'
                  : 'Facturación mensual';

                return (
                  <div
                    key={plan.id}
                    className={`relative rounded-2xl p-5 md:p-6 flex flex-col transition-all duration-300 border-2 ${
                      isHl
                        ? 'bg-primary-600 text-background-50 ring-4 ring-primary-200 scale-[1.02] md:scale-[1.04] z-10 shadow-2xl border-primary-500'
                        : 'bg-background-50 border-background-200 hover:border-primary-200 hover:shadow-lg'
                    }`}
                  >
                    {/* Badge */}
                    {badge && (
                      <div
                        className={`absolute -top-2.5 left-1/2 -translate-x-1/2 px-3.5 py-1 rounded-full text-[10px] font-bold whitespace-nowrap ${
                          isHl ? 'bg-accent-500 text-background-50' : getPlanBadgeStyle(plan)
                        }`}
                      >
                        {badge}
                      </div>
                    )}

                    {/* Name & tagline */}
                    <div className="mb-4 mt-1">
                      <h3 className={`text-lg font-bold mb-0.5 ${isHl ? 'text-background-50' : 'text-foreground-950'}`}>
                        {plan.name}
                      </h3>
                      <p className={`text-[11px] ${isHl ? 'text-background-50/60' : 'text-foreground-400'}`}>
                        {getPlanTagline(plan)}
                      </p>
                    </div>

                    {/* Price */}
                    <div className="mb-3">
                      <div className="flex items-baseline gap-0.5">
                        {isFree ? (
                          <span className={`text-2xl md:text-3xl font-extrabold ${isHl ? 'text-background-50' : 'text-foreground-950'}`}>
                            Gratis
                          </span>
                        ) : (
                          <span className={`text-2xl md:text-3xl font-extrabold ${isHl ? 'text-background-50' : 'text-foreground-950'}`}>
                            {displayPrice}
                          </span>
                        )}
                      </div>
                      <p className={`text-[10px] mt-0.5 ${isHl ? 'text-background-50/50' : 'text-foreground-400'}`}>
                        {displayPeriod}
                      </p>
                    </div>

                    {/* Quick indicators row */}
                    <div className="flex flex-wrap items-center gap-1.5 mb-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-semibold ${
                          activation.approval
                            ? isHl ? 'bg-background-50/15 text-background-50/90' : 'bg-amber-50 text-amber-700 border border-amber-200'
                            : isHl ? 'bg-background-50/15 text-background-50/90' : 'bg-green-50 text-green-700 border border-green-200'
                        }`}
                      >
                        <i className={`${activation.icon} text-[9px]`}></i>
                        {activation.label}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-1.5 mb-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-semibold ${
                        isHl ? 'bg-background-50/10 text-background-50/80' : 'bg-background-100 text-foreground-600'
                      }`}>
                        <i className="ri-hard-drive-2-line text-[9px]"></i>
                        {getPlanStorageLabel(plan)}
                      </span>

                      {plan.has_domain && (
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-semibold ${
                          isHl ? 'bg-background-50/10 text-background-50/80' : 'bg-accent-50 text-accent-700 border border-accent-200'
                        }`}>
                          <i className="ri-global-line text-[9px]"></i>
                          Dominio
                        </span>
                      )}

                      {plan.has_lives && (
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-semibold ${
                          isHl ? 'bg-background-50/10 text-background-50/80' : 'bg-red-50 text-red-600 border border-red-200'
                        }`}>
                          <i className="ri-live-line text-[9px]"></i>
                          Lives
                        </span>
                      )}

                      {plan.has_app && (
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-semibold ${
                          isHl ? 'bg-background-50/10 text-background-50/80' : 'bg-secondary-50 text-secondary-700 border border-secondary-200'
                        }`}>
                          <i className="ri-smartphone-line text-[9px]"></i>
                          App
                        </span>
                      )}

                      {plan.has_managed_streaming && (
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-semibold ${
                          isHl ? 'bg-background-50/10 text-background-50/80' : 'bg-sky-50 text-sky-700 border border-sky-200'
                        }`}>
                          <i className="ri-cloud-line text-[9px]"></i>
                          Streaming
                        </span>
                      )}
                    </div>

                    <p className={`text-xs leading-relaxed mb-4 ${isHl ? 'text-background-50/70' : 'text-foreground-500'}`}>
                      {plan.description}
                    </p>

                    <ul className="space-y-2 mb-6 flex-1">
                      {featureLabels.map((feature) => (
                        <li key={feature.label} className="flex items-start gap-2">
                          <div
                            className={`w-4 h-4 flex items-center justify-center rounded-full shrink-0 mt-0.5 ${
                              feature.negative
                                ? isHl ? 'bg-background-50/10 text-background-50/40' : 'bg-foreground-100 text-foreground-300'
                                : isHl ? 'bg-background-50/20 text-background-50' : getCheckStyle(plan, false)
                            }`}
                          >
                            <i className={`text-[8px] font-bold ${feature.negative ? 'ri-close-line' : 'ri-check-line'}`}></i>
                          </div>
                          <span className={`text-xs leading-snug ${
                            feature.negative
                              ? isHl ? 'text-background-50/40 line-through' : 'text-foreground-400 line-through'
                              : isHl ? 'text-background-50/90' : 'text-foreground-700'
                          }`}>
                            {feature.label}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {plan.price_yearly && !isYearly && (
                      <div className={`rounded-lg p-2 mb-3 text-center ${isHl ? 'bg-background-50/10' : 'bg-green-50 border border-green-100'}`}>
                        <p className={`text-[10px] font-semibold ${isHl ? 'text-background-50/80' : 'text-green-700'}`}>
                          <i className="ri-calendar-check-line mr-1"></i>
                          Licencia anual: {plan.formatted_price_yearly}
                        </p>
                      </div>
                    )}

                    {isYearly && plan.price_yearly && (
                      <div className={`rounded-lg p-2 mb-3 text-center ${isHl ? 'bg-background-50/20' : 'bg-green-100 border border-green-200'}`}>
                        <p className={`text-[10px] font-bold ${isHl ? 'text-background-50' : 'text-green-800'}`}>
                          <i className="ri-check-double-line mr-1"></i>
                          Ahorro anual aplicado
                        </p>
                      </div>
                    )}

                    <button
                      onClick={() => handleSelectPlan(plan)}
                      className={`w-full py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
                        isHl ? 'bg-background-50 text-primary-600 hover:bg-background-100 shadow-lg' : getCtaStyle(plan)
                      }`}
                    >
                      {getPlanCtaText(plan)}
                      <i className="ri-arrow-right-line ml-2"></i>
                    </button>
                  </div>
                );
              })}
            </div>
            )}

            {/* Grid note for responsive */}
            <p className="text-center mt-6 text-[11px] text-foreground-400">
              <i className="ri-information-line mr-1"></i>
              Deslizá horizontalmente en pantallas chicas para ver todos los planes
            </p>
          </div>
        </section>

        {/* ── COMPARISON TABLE ── */}
        <section ref={comparisonRef} id="comparison" className="py-16 md:py-24 bg-background-100/70">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold mb-4">
                <i className="ri-layout-grid-line text-xs"></i>
                Comparación detallada
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
                Compará cada funcionalidad
              </h2>
              <p className="mt-3 text-foreground-500 text-sm max-w-xl mx-auto">
                Filtra por categoría para encontrar exactamente lo que necesitás.
              </p>
            </div>

            {/* Category filter pills */}
            <div className="flex items-center gap-1.5 mb-8 overflow-x-auto pb-2 scrollbar-none flex-wrap justify-center">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`whitespace-nowrap px-3.5 py-2 rounded-full text-xs font-medium cursor-pointer transition-all ${
                    activeCategory === cat
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-background-200 border border-background-200'
                  }`}
                >
                  {cat === 'todas' ? 'Todas' : cat}
                </button>
              ))}
            </div>

            {/* Dynamic comparison table */}
            <div className="bg-background-50 rounded-2xl border border-background-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px]">
                  <thead>
                    <tr className="border-b border-background-200">
                      <th className="text-left px-5 py-4 text-xs font-semibold text-foreground-500 uppercase tracking-wider sticky left-0 bg-background-50 z-10 min-w-[180px]">
                        Funcionalidad
                      </th>
                      {billingPlans.map((plan, idx) => (
                        <th
                          key={plan.id}
                          className={`text-center px-4 py-4 text-xs font-semibold uppercase tracking-wider ${
                            plan.slug === 'pro'
                              ? 'bg-primary-50/50 text-primary-700'
                              : plan.slug === 'business' || plan.slug === 'premium'
                              ? 'text-accent-600'
                              : plan.slug === 'starter'
                              ? 'text-secondary-600'
                              : 'text-foreground-600'
                          } ${idx === 0 ? 'w-[90px]' : 'w-[100px]'}`}
                        >
                          <div className="flex flex-col items-center gap-1">
                            <span>{plan.name}</span>
                            {plan.price_monthly === 0 && (
                              <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[9px] font-semibold">Gratis</span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredComparison.map((feature) => (
                      <tr
                        key={feature.key}
                        className="border-b border-background-100 hover:bg-background-50/60 transition-colors"
                      >
                        <td className="px-5 py-3.5 sticky left-0 bg-background-50">
                          <div>
                            <p className="text-sm font-medium text-foreground-800">{feature.label}</p>
                            {activeCategory === 'todas' && (
                              <p className="text-[10px] text-foreground-400 mt-0.5">{feature.category}</p>
                            )}
                          </div>
                        </td>
                        {billingPlans.map((plan, idx) => {
                          const val = feature.values[plan.slug] ?? feature.values[plan.id];
                          const isHlCol = plan.slug === 'pro';
                          return (
                            <td
                              key={plan.id}
                              className={`px-4 py-3.5 text-center ${isHlCol ? 'bg-primary-50/30' : ''}`}
                            >
                              {val !== undefined ? renderComparisonValue(val) : (
                                <span className="text-xs text-foreground-300">—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredComparison.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-background-200 flex items-center justify-center mb-3">
                    <i className="ri-filter-off-line text-foreground-400 text-xl"></i>
                  </div>
                  <p className="text-sm text-foreground-500">No hay funcionalidades en esta categoría</p>
                </div>
              )}
            </div>

            {/* Legend */}
            <div className="flex items-center justify-center gap-6 mt-5 text-xs text-foreground-400 flex-wrap">
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center">
                  <i className="ri-check-line text-green-600 text-[8px] font-bold"></i>
                </div>
                Incluido
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 rounded-full bg-foreground-100 flex items-center justify-center">
                  <i className="ri-close-line text-foreground-400 text-[10px]"></i>
                </div>
                No incluido
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-foreground-700">Texto</span>
                Valor específico
              </div>
            </div>
          </div>
        </section>

        {/* ── STREAMING HOSTING ── */}
        <StreamingHostingSection />

        {/* ── FAQ ── */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
                <i className="ri-question-answer-line text-xs"></i>
                Preguntas frecuentes
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
                ¿Tenés dudas?
              </h2>
              <p className="mt-3 text-foreground-500 text-sm">
                Respuestas rápidas a las preguntas más comunes sobre los planes.
              </p>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden transition-all"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                  >
                    <span className="text-sm font-semibold text-foreground-800 pr-4">{faq.question}</span>
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all duration-200 ${
                        openFaq === idx
                          ? 'bg-primary-100 text-primary-600 rotate-45'
                          : 'bg-background-100 text-foreground-400'
                      }`}
                    >
                      <i className="ri-add-line text-sm"></i>
                    </div>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── BOTTOM CTA ── */}
        <section className="py-16 md:py-24 bg-background-100/70">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight mb-3">
              ¿Listo para construir tu comunidad?
            </h2>
            <p className="text-foreground-500 text-base mb-8 max-w-xl mx-auto">
              Elegí el plan que mejor se adapte a tu estación. Siempre podés escalar cuando tu comunidad crezca. Recordá: la comunidad es el producto principal.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigate('/register-radio')}
                className="px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-xl transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-primary-500/20 w-full sm:w-auto"
              >
                Crear mi radio gratis
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
              <button
                onClick={scrollToComparison}
                className="px-8 py-3.5 bg-background-50 hover:bg-background-100 text-foreground-700 text-sm font-semibold rounded-xl border border-background-200 transition-all cursor-pointer whitespace-nowrap w-full sm:w-auto"
              >
                <i className="ri-file-list-3-line mr-2"></i>
                Ver comparación completa
              </button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}